/**
 * StockTrailBell — Global anomaly notification bell for master admin (9999313175).
 * - Subscribes to Pusher channel `stock-trail-anomalies`
 * - Shows unread count badge
 * - Click to open popover listing recent open anomalies
 * - Each row: Ack / go to part trail / clear all
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle2, X, ExternalLink } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// All three admins can see the bell now (master + 2 sub-admins).
const ALLOWED_TRAIL_ADMINS = new Set(['9999313175', '9811117751', '9643558419']);
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('session_token') || ''}` });

const StockTrailBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const popRef = useRef(null);

  const enabled = ALLOWED_TRAIL_ADMINS.has(String(user?.mobile || ''));

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await axios.get(`${API}/stock-trail/anomalies?status=open&limit=20`, { headers: auth() });
      setItems(data.items || []);
      setCount(data.open_count || 0);
    } catch { /* ignore */ }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [enabled, load]);

  // Pusher push: refresh + toast
  useEffect(() => {
    if (!enabled) return;
    let p;
    (async () => {
      try {
        const Pusher = (await import('pusher-js')).default;
        const { envChannel } = await import('../utils/pusherEnv');
        p = new Pusher('cfc00fbbcf3d28bd5df1', { cluster: 'ap2' });
        const ch = p.subscribe(envChannel('stock-trail-anomalies'));
        ch.bind('new', (m) => {
          toast.error(`New stock anomaly detected (${m.delta} new)`, {
            action: { label: 'View', onClick: () => navigate('/warehouse/stock-trail/anomalies') },
          });
          load();
        });
      } catch { /* ignore */ }
    })();
    return () => { try { p && p.disconnect(); } catch {} };
  }, [enabled, load, navigate]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const ack = async (id) => {
    try {
      await axios.post(`${API}/stock-trail/anomalies/${id}/ack`, { note: '' }, { headers: auth() });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const ackAll = async () => {
    try {
      await axios.post(`${API}/stock-trail/anomalies/clear-all`, {}, { headers: auth() });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!enabled) return null;

  const sev = { high: 'bg-rose-500', medium: 'bg-amber-500', low: 'bg-sky-500' };

  return (
    <div className="fixed bottom-36 right-4 z-[60]" ref={popRef}>
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="stock-trail-bell"
        className={`relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${count > 0 ? 'bg-rose-500 hover:bg-rose-600' : 'bg-slate-800 hover:bg-slate-700'}`}
        title={`Stock anomalies: ${count}`}
      >
        <Bell className={`w-5 h-5 text-white ${count > 0 ? 'animate-pulse' : ''}`} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-white text-rose-600 text-[11px] font-bold flex items-center justify-center px-1">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute bottom-14 right-0 w-[420px] max-h-[60vh] bg-[#0b0f17] border border-white/10 rounded-xl shadow-2xl overflow-hidden flex flex-col" data-testid="stock-trail-bell-popover">
          <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
            <div>
              <div className="text-sm font-semibold text-white">Stock anomalies</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">{count} open</div>
            </div>
            <div className="flex items-center gap-2">
              {count > 0 && (
                <button onClick={ackAll} data-testid="bell-ack-all" className="text-[11px] text-rose-400 hover:text-rose-300 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Clear all
                </button>
              )}
              <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-slate-500" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-auto divide-y divide-white/5">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-slate-500 text-sm">✅ No anomalies right now</div>
            )}
            {items.map((a) => (
              <div key={a.id} className="px-4 py-3 hover:bg-white/[0.03] text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${sev[a.severity] || sev.low}`} />
                  <span className="text-slate-200 font-medium">{a.kind.replace(/_/g, ' ')}</span>
                  <span className="text-slate-500 ml-auto">{a.hits}× hit{a.hits > 1 ? 's' : ''}</span>
                </div>
                <div className="text-slate-400 font-mono text-[11px]">
                  {a.payload?.jp_part_no || '—'} {a.payload?.location_id ? `@ ${a.payload.location_id}` : ''}
                </div>
                <div className="mt-1 flex items-center gap-3">
                  {a.payload?.jp_part_no && (
                    <button
                      onClick={() => { navigate(`/warehouse/stock-trail/part/${encodeURIComponent(a.payload.jp_part_no)}`); setOpen(false); }}
                      className="text-[11px] text-sky-400 hover:text-sky-300 flex items-center gap-0.5"
                      data-testid={`bell-open-trail-${a.id}`}
                    >
                      <ExternalLink className="w-3 h-3" /> Open trail
                    </button>
                  )}
                  <button onClick={() => ack(a.id)} data-testid={`bell-ack-${a.id}`} className="text-[11px] text-emerald-400 hover:text-emerald-300">Ack</button>
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-white/10 flex items-center justify-between">
            <div className="text-[10px] text-slate-600">Master admin only</div>
            <button
              onClick={() => { navigate('/warehouse/stock-trail/anomalies'); setOpen(false); }}
              data-testid="bell-view-all"
              className="text-[11px] text-slate-400 hover:text-white"
            >
              View all →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockTrailBell;
